import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiJson } from '@/lib/api';
import type {
  CimsoSyncResult,
  DashboardSummary,
  DeskAttachment,
  DeskClient,
  DeskEnquiry,
  DeskEvent,
  DeskFunctionSheet,
  DeskInvoice,
  DeskQuote,
  DeskRateCard,
  DeskSettings,
  DeskVenue,
} from '@/types/desk';

export function useDashboardSummary() {
  return useQuery({
    queryKey: ['dashboard', 'summary'],
    queryFn: () => apiJson<DashboardSummary>('/api/dashboard/summary'),
  });
}

export function useClients(q?: string) {
  const params = q ? `?q=${encodeURIComponent(q)}` : '';
  return useQuery({
    queryKey: ['clients', q || ''],
    queryFn: () =>
      apiJson<{ clients: DeskClient[] }>(`/api/clients${params}`).then(
        (r) => r.clients,
      ),
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<DeskClient> & { company_name: string }) =>
      apiJson<{ client: DeskClient }>('/api/clients', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

export function useEnquiries(status?: string) {
  const params = status ? `?status=${encodeURIComponent(status)}` : '';
  return useQuery({
    queryKey: ['enquiries', status || ''],
    queryFn: () =>
      apiJson<{ enquiries: DeskEnquiry[] }>(`/api/enquiries${params}`).then(
        (r) => r.enquiries,
      ),
  });
}

export function useCreateEnquiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<{ enquiry: DeskEnquiry }>('/api/enquiries', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['enquiries'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useConvertEnquiry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<{ enquiry: DeskEnquiry; event: DeskEvent }>(
        `/api/enquiries/${id}/convert`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['enquiries'] });
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useEvents(status?: string) {
  const params = status ? `?status=${encodeURIComponent(status)}` : '';
  return useQuery({
    queryKey: ['events', status || ''],
    queryFn: () =>
      apiJson<{ events: DeskEvent[] }>(`/api/events${params}`).then(
        (r) => r.events,
      ),
  });
}

export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: ['events', 'detail', id || ''],
    enabled: Boolean(id),
    queryFn: () =>
      apiJson<{ event: DeskEvent }>(`/api/events/${id}`).then((r) => r.event),
  });
}

export function useEventFunctionSheet(id: string | undefined) {
  return useQuery({
    queryKey: ['events', 'function-sheet', id || ''],
    enabled: Boolean(id),
    queryFn: () =>
      apiJson<{
        event: Partial<DeskEvent>;
        functionSheet: DeskFunctionSheet | null;
        cimsoFieldsReadOnly: boolean;
      }>(`/api/events/${id}/function-sheet`),
    retry: false,
  });
}

export function useCimsoSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      dryRun?: boolean;
      daysBack?: number;
      daysForward?: number;
      maxBookings?: number;
      listOnly?: boolean;
      debug?: boolean;
      mode?: 'incremental' | 'backfill' | 'full';
      mirrorCatalog?: boolean;
    }) =>
      apiJson<CimsoSyncResult>('/api/cimso/sync', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (result) => {
      if (!result.dryRun && result.status !== 'disabled') {
        void qc.invalidateQueries({ queryKey: ['events'] });
        void qc.invalidateQueries({ queryKey: ['clients'] });
        void qc.invalidateQueries({ queryKey: ['dashboard'] });
        void qc.invalidateQueries({ queryKey: ['cimso', 'capabilities'] });
      }
    },
  });
}

export function useCimsoCapabilities() {
  return useQuery({
    queryKey: ['cimso', 'capabilities'],
    queryFn: () =>
      apiJson<{
        capabilities: Array<{
          message: string;
          status: string;
          error_code: number | null;
          detail: string | null;
          field_names: string[];
          last_probed_at: string | null;
        }>;
      }>('/api/cimso/capabilities'),
    retry: false,
  });
}

export function useCimsoCapabilitiesProbe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiJson<{
        status: string;
        probed: number;
        byStatus: Record<string, number>;
      }>('/api/cimso/capabilities', { method: 'POST', body: '{}' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cimso', 'capabilities'] });
    },
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<{ event: DeskEvent }>('/api/events', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['events'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useVenues() {
  return useQuery({
    queryKey: ['venues'],
    queryFn: () =>
      apiJson<{ venues: DeskVenue[] }>('/api/venues').then((r) => r.venues),
    retry: false,
  });
}

export function useEventAttachments(eventId: string | undefined) {
  return useQuery({
    queryKey: ['attachments', 'event', eventId || ''],
    enabled: Boolean(eventId),
    queryFn: () =>
      apiJson<{ attachments: DeskAttachment[] }>(
        `/api/attachments?entity_type=event&entity_id=${encodeURIComponent(eventId!)}`,
      ).then((r) => r.attachments),
  });
}

export function useAttachmentDownload() {
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<{ url: string; filename: string }>(
        `/api/attachments/${id}/download`,
      ),
  });
}

export function useOrgSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () =>
      apiJson<{ settings: DeskSettings }>('/api/settings').then(
        (r) => r.settings,
      ),
    retry: false,
  });
}

export function useUpdateOrgSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<DeskSettings>) =>
      apiJson<{ settings: DeskSettings }>('/api/settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function useQuotes() {
  return useQuery({
    queryKey: ['quotes'],
    queryFn: () =>
      apiJson<{ quotes: DeskQuote[] }>('/api/quotes').then((r) => r.quotes),
  });
}

export function useCreateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<{ quote: DeskQuote }>('/api/quotes', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quotes'] });
      void qc.invalidateQueries({ queryKey: ['clients'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useIssueInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quoteId: string) =>
      apiJson<{ invoice: DeskInvoice }>(
        `/api/quotes/${quoteId}/issue-invoice`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quotes'] });
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: () =>
      apiJson<{ invoices: DeskInvoice[] }>('/api/invoices').then(
        (r) => r.invoices,
      ),
  });
}

export function useRateCards() {
  return useQuery({
    queryKey: ['rate-cards'],
    queryFn: () =>
      apiJson<{ rateCards: DeskRateCard[] }>('/api/rate-cards').then(
        (r) => r.rateCards,
      ),
  });
}

export function useCreateRateCard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<{ rateCard: DeskRateCard }>('/api/rate-cards', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['rate-cards'] }),
  });
}
